/**
 * Shared JSON REST client for Mirrably data providers.
 *
 * Product shapes stay domain-specific; HTTP behavior belongs in core.
 */

import { DataProviderError } from './provider.js';
import type { MediaPort } from '../ports/media-port.js';
import { BrowserMediaPort } from '../ports/browser-media-port.js';

/** Configuration for shared JSON REST calls. */
export interface RestClientConfig {
  /** Base URL, e.g. "https://api.example.com/v1". No trailing slash. */
  baseUrl: string;
  /** Optional API key sent as X-API-Key header. */
  apiKey?: string;
  /** Request timeout in milliseconds. Defaults to 10 000. */
  timeoutMs?: number;
  /**
   * Optional MediaPort to use for network requests.
   * Defaults to BrowserMediaPort (uses globalThis.fetch).
   */
  mediaPort?: MediaPort;
}

export class JsonRestClient {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;
  private readonly timeoutMs: number;
  private readonly mediaPort: MediaPort;

  constructor(config: RestClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.timeoutMs = config.timeoutMs ?? 10_000;
    this.mediaPort = config.mediaPort ?? new BrowserMediaPort();
    this.headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(config.apiKey ? { 'X-API-Key': config.apiKey } : {}),
    };
  }

  async get<T>(path: string): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);

    let response: Response;
    try {
      response = await this.mediaPort.fetch(url, {
        method: 'GET',
        headers: this.headers,
        signal: controller.signal,
      });
    } catch (err) {
      clearTimeout(timer);
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new DataProviderError(
          `Request timed out after ${this.timeoutMs}ms: GET ${url}`,
          undefined,
          err,
        );
      }
      throw new DataProviderError(
        `Network error: GET ${url} — ${String(err)}`,
        undefined,
        err,
      );
    } finally {
      clearTimeout(timer);
    }

    if (!response.ok) {
      let body = '';
      try {
        body = await response.text();
      } catch {
        // Ignore body read errors.
      }
      throw new DataProviderError(
        `HTTP ${response.status} from GET ${url}: ${body.slice(0, 200)}`,
        response.status,
      );
    }

    try {
      return (await response.json()) as T;
    } catch (err) {
      throw new DataProviderError(
        `Invalid JSON from GET ${url}`,
        response.status,
        err,
      );
    }
  }
}
